import { errorBlockingSelf } from './getMailboxes.locstring.json';
import loc, { format } from 'owa-localize';
import { confirm, ConfirmCustomizationOptions } from 'owa-confirm-dialog';
import { isItemOfMessageType, MailRowDataPropertyGetter, TableView } from 'owa-mail-list-store';
import mailStore from 'owa-mail-store/lib/store/Store';
import EmailAddressWrapper from 'owa-service/lib/contract/EmailAddressWrapper';
import Message from 'owa-service/lib/contract/Message';

import isSenderSelf from 'owa-mail-store/lib/selectors/isSenderSelf';

/**
 * Get the de-duped senders given the ids
 * @param mailboxes the mailboxes to be sweeped
 * @return the de-duped sweepable mailboxe based on smtp address, null if
 * the email addresses contains user's own email address
 */
export function getFilteredSweepableMailboxes(
    mailboxes: EmailAddressWrapper[]
): EmailAddressWrapper[] {
    // Use a dictionary is to de-dup the mailboxes smtp addresses
    const senderInfoDictionary: { [id: string]: boolean } = {};
    const filteredMailboxes: EmailAddressWrapper[] = [];

    for (let i = 0; i < mailboxes.length; i++) {
        const emailAddress = mailboxes[i].EmailAddress;
        if (!emailAddress) {
            // Skip the current iteration if there is no valid email address.
            // For example, for emails inside Conversation History, there is no EmailAddress in LastSender's mailbox.
            continue;
        }

        // Show error dialog and early return if the email addresses to block contain user self's email address
        if (isSenderSelf(emailAddress)) {
            confirm(
                format(loc(errorBlockingSelf), emailAddress),
                null,
                false /* resolveImmediately */,
                <ConfirmCustomizationOptions>{
                    hideCancelButton: true,
                }
            );

            return null;
        }

        const smtpAddressLowerCase = emailAddress.toLowerCase();

        // De-dup the mailboxes
        if (!senderInfoDictionary[smtpAddressLowerCase]) {
            // Add smtp address to dictionary if there's no dup
            senderInfoDictionary[smtpAddressLowerCase] = true;
            filteredMailboxes.push(mailboxes[i]);
        }
    }

    // The filtered mailbox can be empty in case there is no EmailAddress in all
    // LastSenders' mailboxes, e.g conversations in Conversation History
    return filteredMailboxes.length > 0 ? filteredMailboxes : null;
}

export function getLastSenderMailboxesFromRowKeys(
    rowKeys: string[],
    tableView: TableView
): EmailAddressWrapper[] {
    const mailboxes: EmailAddressWrapper[] = [];
    rowKeys.forEach(rowKey => {
        const lastSenderMailbox = MailRowDataPropertyGetter.getLastSenderMailbox(rowKey, tableView);
        if (lastSenderMailbox) {
            mailboxes.push(lastSenderMailbox);
        }
    });

    // Only add mailbox if lastSender is valid. lastSender could be undefined in rowModified notification payload if the message is directly POSTed
    // VSO: 19871 - Sender MBX are already de-duped, make sure this works when we have multi-value sorted tables
    // last sender is same for rows with same conversationId, Discuss how to implement this
    // for multi-value sorts
    return getFilteredSweepableMailboxes(mailboxes);
}

/**
 * Get the mailboxes to block from the selected conversations
 * @param itemIds the item ids
 * @return the filtered sweepable mailboxes
 */
export function tryGetMailboxesFromItemIds(itemIds: string[]): EmailAddressWrapper[] {
    const mailboxes: EmailAddressWrapper[] = [];
    itemIds.forEach(itemId => {
        const item = mailStore.items.get(itemId);
        if (item && isItemOfMessageType(item)) {
            mailboxes.push((item as Message).From.Mailbox);
        }
    });

    return getFilteredSweepableMailboxes(mailboxes);
}
